import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, ArrowRight, Ban, CalendarDays, OctagonAlert, Camera, ChevronDown, ChevronLeft, ChevronRight, CircleCheck, CircleX, ClipboardList, DoorClosed, LifeBuoy, MapPin, Package, Pause, Repeat, StickyNote, Truck, TriangleAlert, UserCheck, Warehouse, Wrench } from 'lucide-react';
import { SLOT_LABEL, STATUSES, STATUS_LABEL, formatDateTime, formatDay, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import JobPhotos from '../../components/JobPhotos.jsx';
import Modal from '../../components/Modal.jsx';
import RouteMap from '../../components/RouteMap.jsx';
import Pager, { pageCount, pageSlice } from '../../components/Pager.jsx';
import { useAutoRefresh } from '../../useAutoRefresh.js';
import { addDays } from '../bookings/shared.jsx';
import { problem } from '../bookings/util.js';

// Operations > Pulse: every update logged on the jobs during a day, newest
// first: on route, arrived, photos, completed, incidents... Filter by team,
// person, the job's status just after each update, and kind of event (none ticked = all). Refreshes itself every 30 seconds.
// Click a line to open its job: that day's timeline, photos, where the
// phone was on route and on arrival, and notes.
const EVENTS = [
  { id: 'on_route', label: 'On route', icon: Truck, kinds: ['on_route'] },
  { id: 'arrived', label: 'Arrived', icon: MapPin, kinds: ['arrived'] },
  { id: 'started', label: 'Started', icon: Wrench, kinds: ['started'] },
  { id: 'completed', label: 'Completed', icon: CircleCheck, kinds: ['completed'] },
  { id: 'not_done', label: 'Not done', icon: CircleX, kinds: ['not_done'] },
  { id: 'paused', label: 'Paused / resumed', icon: Pause, kinds: ['paused', 'resumed'] },
  { id: 'photo', label: 'Photos', icon: Camera, kinds: ['photo', 'photo_removed'] },
  { id: 'defect', label: 'Defects', icon: OctagonAlert, kinds: ['defect'] },
  { id: 'incident', label: 'Incidents', icon: TriangleAlert, kinds: ['incident'] },
  { id: 'support', label: 'Support', icon: LifeBuoy, kinds: ['support'] },
  { id: 'switched', label: 'Switched job', icon: Repeat, kinds: ['switched'] },
  { id: 'storeroom', label: 'At a storeroom', icon: Warehouse, kinds: ['storeroom'] },
  { id: 'stock', label: 'Stock', icon: Package, kinds: ['stock'] },
  { id: 'note', label: 'Notes', icon: StickyNote, kinds: ['note'] },
  { id: 'office', label: 'Office changes', icon: ClipboardList, kinds: ['office'] },
];
const EVENT_OF = Object.fromEntries(EVENTS.flatMap((e) => e.kinds.map((k) => [k, e])));
// A picture for each job status: a line that moved the job shows where it
// was, an arrow, and where it is now (Assigned -> van for On route).
const STATUS_ICON = {
  booked: CalendarDays, assigned: UserCheck, on_the_way: Truck, in_progress: Wrench, paused: Pause,
  completed: CircleCheck, aborted: CircleX, no_access: DoorClosed, cancelled: Ban,
};
const moved = (p) => p.statusBefore && p.status && p.statusBefore !== p.status && STATUS_ICON[p.statusBefore] && STATUS_ICON[p.status];
// The arrow says the move, so "Status: Assigned to On route (...)" reads "On route (...)".
const words = (p) => (moved(p) ? p.text.replace(/^Status: .+? to /, '') : p.text);

function Mark({ p }) {
  if (moved(p)) {
    const From = STATUS_ICON[p.statusBefore];
    const To = STATUS_ICON[p.status];
    return (
      <span className="pulse-move" title={`${STATUS_LABEL[p.statusBefore]} to ${STATUS_LABEL[p.status]}`}>
        <span className={`pulse-icon si-${p.statusBefore}`}><From size={16} aria-hidden="true" /></span>
        <ArrowRight size={14} aria-hidden="true" className="pulse-arrow" />
        <span className={`pulse-icon si-${p.status}`}><To size={16} aria-hidden="true" /></span>
      </span>
    );
  }
  const ev = EVENT_OF[p.kind] || EVENT_OF.office;
  return <span className="pulse-icon" title={ev.label}><ev.icon size={16} aria-hidden="true" /></span>;
}
const time = (v) => new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' }).format(new Date(v));
const dayName = (d) => new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));

// Events: a button that opens a list of tick boxes, one per kind of event,
// each with how many there are. Nothing ticked shows every event; ticking
// narrows the feed to those.
function EventFilter({ events, counts, onToggle, onSet }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const label = events.size === 0 ? 'All' : events.size === 1 ? EVENTS.find((e) => events.has(e.id)).label : `${events.size} selected`;
  return (
    <div className="event-filter" ref={box}>
      <button type="button" className="btn with-icon" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Events: {label} <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <div className="event-filter-panel" role="group" aria-label="Events">
          {events.size > 0 && (
            <div className="event-filter-quick">
              <button type="button" className="link-btn" onClick={() => onSet(new Set())}>Clear</button>
            </div>
          )}
          {EVENTS.map((e) => (
            <label key={e.id} className="event-filter-row">
              <input type="checkbox" checked={events.has(e.id)} onChange={() => onToggle(e.id)} />
              <e.icon size={15} aria-hidden="true" />
              <span>{e.label}</span>
              <span className="event-filter-n">{counts[e.id] || 0}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const far = (m) => (m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`);
const WHERE = { on_the_way: { label: 'On route', mark: 'R', colour: '#8a5a00' }, in_progress: { label: 'Arrived', mark: 'A', colour: '#1f4fa8' } };
const HOME_COLOUR = '#0B2340';

// One job as Pulse opens it, read only.
function PulseJob({ id, day, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => api(`/pulse/jobs/${id}?day=${day}`).then((d) => { setData(d); setError(null); }, (e) => setError(problem(e))), [id, day]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);
  const j = data?.job;
  const whereText = (l) => `${WHERE[l.kind]?.label || l.kind} ${time(l.at)}, ${l.who}${l.distance != null ? `, ${far(l.distance)} away` : ''}${l.accuracy != null ? ` (GPS ±${far(l.accuracy)})` : ''}`;
  const stops = data ? [
    ...(j.target ? [{ key: 'home', coord: [j.target.lng, j.target.lat], label: '⌂', colour: HOME_COLOUR, title: j.target.exact ? 'The address' : 'Middle of the postcode' }] : []),
    ...data.locations.map((l, i) => ({ key: `l${i}`, coord: [l.lng, l.lat], label: WHERE[l.kind]?.mark || '•', colour: WHERE[l.kind]?.colour || '#666', title: whereText(l) })),
  ] : [];
  return (
    <Modal open wide title={j ? `${j.reference} · ${j.address}, ${j.postcode}` : 'Loading…'} onClose={onClose}
      footer={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      {error && <p className="notice-line notice-bad">{error}</p>}
      {!data && !error && <p className="muted">Loading…</p>}
      {data && (
        <div className="stack pulse-job">
          <dl className="facts">
            <dt>Status now</dt>
            <dd><span className={`status status-${j.status}`}>{STATUS_LABEL[j.status]}</span>{j.statusReason ? ` ${j.statusReason}` : ''}</dd>
            <dt>Job</dt><dd>{[j.type.client, j.type.workType].filter(Boolean).join(' · ')}</dd>
            <dt>Booked for</dt><dd>{j.bookingDate ? formatDay(j.bookingDate) : 'No date'}{j.slot ? ` · ${SLOT_LABEL[j.slot]}` : ''}{j.teamName ? ` · ${j.teamName}` : ''}</dd>
            {j.accessNotes && <><dt>Access</dt><dd>{j.accessNotes}</dd></>}
            {j.jobNotes && <><dt>Job notes</dt><dd>{j.jobNotes}</dd></>}
          </dl>

          <section>
            <h3>{dayName(day)} {formatDay(day)}</h3>
            {!data.timeline.length && <p className="muted">Nothing logged on this job on this day.</p>}
            <ol className="pulse-list">
              {data.timeline.map((t) => {
                const ev = EVENT_OF[t.kind] || EVENT_OF.office;
                return (
                  <li key={t.id} className={`pulse-item k-${ev.id}`}>
                    <span className="pulse-time">{time(t.at)}</span>
                    <Mark p={t} />
                    <span className="pulse-body"><span className="pulse-text"><strong>{t.who || 'Someone'}</strong> {words(t)}</span></span>
                    <span className={`status status-${t.status}`}>{STATUS_LABEL[t.status]}</span>
                  </li>
                );
              })}
            </ol>
            {data.otherDays > 0 && <p className="small muted">{data.otherDays} more update{data.otherDays === 1 ? '' : 's'} on other days.</p>}
          </section>

          {data.locations.length > 0 && (
            <section>
              <h3>Where the phone was</h3>
              <RouteMap height={280} stops={stops} fullscreen />
              <ul className="pulse-where small">
                {j.target && <li><span className="pulse-mark" style={{ background: HOME_COLOUR }}>⌂</span> {j.target.exact ? 'The address' : 'Middle of the postcode'}</li>}
                {data.locations.map((l, i) => (
                  <li key={i}><span className="pulse-mark" style={{ background: WHERE[l.kind]?.colour || '#666' }}>{WHERE[l.kind]?.mark || '•'}</span> {whereText(l)}</li>
                ))}
              </ul>
            </section>
          )}

          <JobPhotos key={id} base={`/pulse/jobs/${id}/photos`} title="Photos" />

          {data.notes.length > 0 && (
            <section>
              <h3>Office notes ({data.notes.length})</h3>
              <ul className="history">
                {data.notes.map((n, i) => <li key={i}><span>{n.body}</span><span className="small muted">{n.by} · {formatDateTime(n.at)}</span></li>)}
              </ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function Pulse() {
  const [params, setParams] = useSearchParams();
  const day = params.get('day') || todayUk();
  const setDay = (d) => setParams({ day: d }, { replace: true });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [team, setTeam] = useState(''); // '' all, 'none', or a team id
  const [person, setPerson] = useState(''); // '' everyone, or a name as the lines show it
  const [status, setStatus] = useState('');
  const [events, setEvents] = useState(() => new Set()); // none ticked = every event
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [openJob, setOpenJob] = useState(null);
  // A new day or filter starts at the first page.
  useEffect(() => { setPage(1); }, [day, team, person, status, events, size]);

  const load = useCallback(() => api(`/pulse?day=${day}`).then((d) => { setData(d); setError(null); }, (e) => setError(problem(e))), [day]);
  useEffect(() => { setData(null); load(); }, [load]);
  useAutoRefresh(load);

  const teamName = (id) => data?.teams.find((t) => t.id === id)?.name || null;
  // Team, person and status first, so each event chip counts what it would add.
  const inTeam = useMemo(() => (data?.pulse || []).filter((p) => (
    (!team || (team === 'none' ? !p.teamId : p.teamId === Number(team))) && (!status || p.status === status)
  )), [data, team, status]);
  // Everyone who logged something in the team and status picked, most updates first.
  const people = useMemo(() => {
    const n = inTeam.reduce((m, p) => m.set(p.who || 'Someone', (m.get(p.who || 'Someone') || 0) + 1), new Map());
    return [...n].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [inTeam]);
  const scoped = useMemo(() => (person ? inTeam.filter((p) => (p.who || 'Someone') === person) : inTeam), [inTeam, person]);
  const counts = useMemo(() => scoped.reduce((m, p) => { const e = EVENT_OF[p.kind]?.id || 'office'; m[e] = (m[e] || 0) + 1; return m; }, {}), [scoped]);
  const shown = events.size ? scoped.filter((p) => events.has(EVENT_OF[p.kind]?.id || 'office')) : scoped;
  // Refreshing can shorten the list (a filter, a day with fewer): stay on a page that exists.
  const onPage = Math.min(page, pageCount(shown.length, size));
  const visible = pageSlice(shown, onPage, size);
  const goTo = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const toggle = (id) => setEvents((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const filtered = team || person || status || events.size > 0;
  const reset = () => { setTeam(''); setPerson(''); setStatus(''); setEvents(new Set()); };

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Pulse</h1>
            <p className="muted">Every update on the jobs as it happens: on route, arrived, photos, completed and more, newest first. Pick event types under Events to narrow it down.</p>
          </div>
        </div>
        <div className="filters">
          <div className="week-nav" role="group" aria-label="Day">
            <button type="button" className="btn icon-only" aria-label="Previous day" onClick={() => setDay(addDays(day, -1))}><ChevronLeft size={18} aria-hidden="true" /></button>
            <button type="button" className="btn" onClick={() => setDay(todayUk())}>Today</button>
            <button type="button" className="btn icon-only" aria-label="Next day" onClick={() => setDay(addDays(day, 1))}><ChevronRight size={18} aria-hidden="true" /></button>
            <input type="date" className="filter-date" value={day} aria-label="Day" onChange={(e) => /^\d{4}-\d{2}-\d{2}$/.test(e.target.value) && setDay(e.target.value)} />
            <span className="week-label">{dayName(day)} {formatDay(day)}</span>
          </div>
          <select value={team} onChange={(e) => setTeam(e.target.value)} aria-label="Team">
            <option value="">All teams</option>
            {(data?.teams || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            <option value="none">Not on a team</option>
          </select>
          <select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Person">
            <option value="">Everyone</option>
            {people.map(([name, n]) => <option key={name} value={name}>{name} ({n})</option>)}
            {person && !people.some(([name]) => name === person) && <option value={person}>{person} (0)</option>}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Job status">
            <option value="">Any job status</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <EventFilter events={events} counts={counts} onToggle={toggle} onSet={setEvents} />
          {filtered && <button type="button" className="btn" onClick={reset}>Clear filters</button>}
        </div>
        {error && <p className="notice-line notice-bad">{error}</p>}
      </section>

      <section className="card">
        <div className="pulse-head">
          <h2><Activity size={20} aria-hidden="true" /> {data ? `${shown.length} update${shown.length === 1 ? '' : 's'}` : 'Loading…'}</h2>
        </div>
        {data && !shown.length && <p className="muted">{data.pulse.length ? 'Nothing matches these filters.' : 'Nothing logged on any job on this day yet.'}</p>}
        <ol className="pulse-list">
          {visible.map((p) => {
            const ev = EVENT_OF[p.kind] || EVENT_OF.office;
            return (
              <li key={p.id} className={`pulse-item pulse-open k-${ev.id}`} role="button" tabIndex={0}
                title={`Open ${p.reference}`} onClick={() => setOpenJob(p.bookingId)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenJob(p.bookingId); } }}>
                <span className="pulse-time">{p.count > 1 ? `${time(p.firstAt)}–${time(p.at)}` : time(p.at)}</span>
                <Mark p={p} />
                <span className="pulse-body">
                  <span className="pulse-text"><strong>{p.who || 'Someone'}</strong> {words(p)}</span>
                  <span className="small muted">
                    {p.reference} · {p.address}, {p.postcode}{teamName(p.teamId) ? ` · ${teamName(p.teamId)}` : ''}{p.bookingDate && p.bookingDate !== day ? ` · booked for ${formatDay(p.bookingDate)}` : ''}
                  </span>
                </span>
                <span className={`status status-${p.status}`}>{STATUS_LABEL[p.status]}</span>
              </li>
            );
          })}
        </ol>
        <Pager total={shown.length} page={onPage} size={size} onPage={goTo} onSize={setSize} label="updates" />
      </section>
      {openJob && <PulseJob id={openJob} day={day} onClose={() => setOpenJob(null)} />}
    </div>
  );
}
